<template>
  <div>
    <Popover class="relative hidden lg:block">
      <PopoverButton
        class="submenu-button flex items-center gap-x-1 focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        :class="linkClass"
      >
        {{ item.label }}
        <ChevronDownIcon
          class="h-5 w-5 flex-none text-gray-400"
          aria-hidden="true"
        />
      </PopoverButton>

      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-1"
      >
        <PopoverPanel
          as="ul"
          class="absolute top-full z-10 mt-2 w-56 border border-gray-200 bg-white p-2 shadow-lg"
          :class="isLastItem ? '-right-4' : '-left-2'"
        >
          <PopoverButton
            v-for="subItem in item.items"
            :key="subItem._uid"
            as="li"
            class="px-3 py-2 hover:bg-gray-50"
            :class="{
              'flex justify-center': subItem.component === 'CallToActionButton',
            }"
          >
            <component
              :is="subItem.component"
              :item="subItem"
              :blok="{ ...blok, bgVariant: 'white' }"
              :context="context"
              :link-class="`${linkColor(subItem)} inline-block w-full`"
              class="menu-subitem"
            />
          </PopoverButton>
        </PopoverPanel>
      </transition>
    </Popover>

    <!-- Mobile submenu -->
    <Disclosure
      v-slot="{ open }"
      as="div"
      class="flex w-full flex-col items-center lg:hidden"
    >
      <DisclosureButton
        class="submenu-button flex w-full items-center justify-between text-left text-xl font-bold focus-visible:outline focus-visible:outline-current"
        :class="linkClass"
      >
        {{ item.label }}
        <component
          :is="open ? MinusIcon : PlusIcon"
          aria-hidden="true"
          class="h-7 w-7"
        />
        <!-- <ChevronDownIcon
        :class="[open ? 'rotate-180' : '', 'h-5 w-5 flex-none']"
        aria-hidden="true"
      /> -->
      </DisclosureButton>
      <DisclosurePanel class="mt-2 w-full space-y-2 pl-6" as="ul">
        <li
          v-for="(subItem, index) in item.items"
          :key="subItem._uid"
          class="flex px-3 py-2"
          :class="{ 'border-b': index < item.items.length - 1 }"
        >
          <component
            :is="subItem.component"
            :item="subItem"
            :blok="blok"
            :context="context"
            :link-class="`${linkClass} w-full`"
            class="w-full text-lg"
            @click.capture="closeMobileMenu"
            @keypress.enter="closeMobileMenu"
          />
        </li>
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import CallToActionLink from './CallToActionLink.vue'
import CallToActionButton from './CallToActionButton.vue'
import { PlusIcon, MinusIcon } from '@heroicons/vue/24/solid'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
  item: {
    type: Object,
    default: () => {},
  },
  isLastItem: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['closeMenu'])
// All of these components can be added dynamically
defineOptions({
  components: {
    CallToActionButton,
    CallToActionLink,
  },
})

const { textColorClass, isBackgroundDark } = useColorPalette()

const linkClass = computed(() => {
  if (props.blok.linkColor) {
    return textColorClass(props.blok.linkColor)
  }
  return isBackgroundDark(props.blok.bgVariant)
    ? 'text-gray-100 hover:text-white focus:text-white'
    : 'text-gray-800 hover:text-gray-900 focus:text-gray-900'
})

function closeMobileMenu() {
  emit('closeMenu')
}
function linkColor(item) {
  return item.linkTextColor
    ? textColorClass(item.linkTextColor)
    : 'text-gray-800'
}
</script>

<style>
.submenu-button:hover,
.submenu-button:focus {
  text-shadow: 0 0 0.8px;
}

.menu-subitem > div {
  width: 100%;
}
</style>
